import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Combobox as ComboboxRoot,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { cn } from '@/lib/utils'

// `disabled` shows an option that cannot be picked yet; `hint` says why, next to its label.
type Option = { value: string; label: string; disabled?: boolean; hint?: string }

const CREATE_PREFIX = '__create__'

export function Combobox(props: {
  id?: string
  value: string
  onChange: (value: string, selectedValue: string | undefined) => void
  options: Option[]
  placeholder?: string
  searchPlaceholder: string
  commandEmpty: string
  creatable?: boolean
  createLabel?: (value: string) => string
  onCreateNew?: (value: string) => void
  variant?: 'default' | 'outline'
  isError?: boolean
  children?: React.ReactNode
}) {
  const [searchValue, setSearchValue] = useState('')

  const optionIds = new Set(props.options.map((o) => o.value))
  const baseOptions =
    props.creatable && props.value && !optionIds.has(props.value)
      ? [...props.options, { value: props.value, label: props.value }]
      : props.options

  const hasExactMatch = baseOptions.some(
    (o) => o.value.toLowerCase() === searchValue.toLowerCase() || o.label.toLowerCase() === searchValue.toLowerCase(),
  )
  const showCreateOption = props.creatable && searchValue.trim() && !hasExactMatch

  const allItems = !showCreateOption
    ? baseOptions
    : [
        ...baseOptions,
        {
          value: `${CREATE_PREFIX}${searchValue}`,
          label: props.createLabel ? props.createLabel(searchValue) : `Create "${searchValue}"`,
        },
      ]

  const selectedItem = baseOptions.find((o) => o.value === props.value) ?? null

  return (
    <ComboboxRoot
      // Non-modal: a modal popup marks the surrounding form/dialog inert, hiding the other fields and the submit
      // button from the a11y tree and from pointer events until dismissed. Outside-click / Escape still close it.
      modal={false}
      items={allItems}
      value={selectedItem}
      onValueChange={(newItem: Option | null) => {
        // Don't reset value when options haven't loaded yet
        if (newItem === null && props.options.length === 0) return
        let newValue = newItem?.value ?? ''
        if (newValue.startsWith(CREATE_PREFIX)) {
          newValue = newValue.slice(CREATE_PREFIX.length)
          if (props.onCreateNew) {
            props.onCreateNew(newValue)
            if (props.creatable) setSearchValue('')
            return
          }
        }
        if (newValue === props.value) return
        props.onChange(newValue, newValue)
        if (props.creatable) setSearchValue('')
      }}
      onInputValueChange={props.creatable ? (inputValue) => setSearchValue(inputValue) : undefined}
      isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
      filter={
        props.creatable
          ? (item: Option, query: string) => {
              if (item.value.startsWith(CREATE_PREFIX)) return true
              if (!query) return true
              return item.label.toLowerCase().includes(query.toLowerCase())
            }
          : undefined
      }
    >
      {props.children ?? (
        <ComboboxTrigger
          render={
            <Button
              id={props.id}
              type="button"
              variant="outline"
              aria-invalid={props.isError || undefined}
              className={cn('h-8 w-full justify-between font-normal', !selectedItem && 'text-muted-foreground')}
            />
          }
        >
          {selectedItem ? selectedItem.label : <span>{props.placeholder}</span>}
        </ComboboxTrigger>
      )}
      <ComboboxContent className="min-w-(--anchor-width)">
        <ComboboxInput showTrigger={false} placeholder={props.searchPlaceholder} />
        <ComboboxEmpty>{props.commandEmpty}</ComboboxEmpty>
        <ComboboxList>
          {(item: Option) => (
            <ComboboxItem key={item.value} value={item} disabled={item.disabled}>
              {item.label}
              {item.hint && <span className="text-muted-foreground ml-auto text-xs">{item.hint}</span>}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  )
}
